// src/features/analytics/components/MediaAudienceDrawer.tsx
//
// Content-tab drill-down (Seq U-5): clicking a top-content row opens this
// right Drawer with the row's headline totals and a per-media audience
// breakdown (Country | Region | City, + Embed domain behind the metrics
// expansion) over the selected range. The expansion also adds the peak
// concurrency headline/trend and the VOD retention panel. Mirrors the legacy
// media drawer customers know; every query is enabled only while open.

import { Suspense, lazy, useState } from 'react';
import { Button, Drawer, Group, SegmentedControl, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core';
import { DateTime } from 'luxon';
import {
  ANALYTICS_GEO_DIMENSIONS,
  ANALYTICS_MEDIA_AUDIENCE_DIMENSIONS,
  ANALYTICS_MEDIA_AUDIENCE_DIMENSIONS_LEGACY,
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  type AnalyticsGeoDimension,
  type AnalyticsMediaAudienceDimension,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import type { UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { DIMENSION_LABELS } from '../audienceValues';
import { rangePeakProvisional } from '../mediaPeaks';
import { useAnalyticsMediaAudience } from '../useAnalyticsMediaAudience';
import { useAnalyticsMediaPeaks } from '../useAnalyticsMediaPeaks';
import { useAnalyticsMediaRetention } from '../useAnalyticsMediaRetention';
import { useAnalyticsMeta } from '../useAnalyticsMeta';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { ANALYTICS_PRESET_LABELS } from '../searchParams';
import type { TopContentRow } from '../types';
import { AudienceBreakdownPanel } from './AudienceBreakdownPanel';
import { MediaPeaksPanel } from './MediaPeaksPanel';
import { MediaRetentionPanel } from './MediaRetentionPanel';
import { StatTile } from './StatTile';

// Same lazy split point as the Audience tab — the map chunks load only when
// a user flips a geo panel to Map (§4.2 entry-bundle gate).
const AudienceMapPanel = lazy(() => import('../map/AudienceMapPanel'));

interface MediaAudienceDrawerProps {
  /** Account cid owning the media (account scope only in v1). */
  cid: number | null;
  /** The clicked top-content row; null keeps the drawer closed and its queries disabled. */
  row: TopContentRow | null;
  /** Floored fetch range — queries send this (the gateway rejects pre-window). */
  range: UtcRange;
  /** What the customer selected (unfloored) — coverage notices name this. */
  selectedRange: UtcRange;
  onClose: () => void;
  /** Drill-in to the per-stream page (dashboard ADR-009); absent while the gateway lacks `features.hourly`. */
  onOpenStreamPage?: (row: TopContentRow) => void;
}

const GEO_EMPTY_MESSAGE = 'No geo breakdown for this media in the selected range.';
const EMBED_EMPTY_MESSAGE = 'No embed-domain breakdown for this media in the selected range.';

/** Peak minutes are points in time — rendered in the viewer's LOCAL timezone (ADR-003 §4). */
function peakMinuteLabel(seconds: number): string {
  return DateTime.fromSeconds(seconds).toFormat('MMM d, h:mm a');
}

export function MediaAudienceDrawer({ cid, row, range, selectedRange, onClose, onOpenStreamPage }: MediaAudienceDrawerProps) {
  const [dimension, setDimension] = useState<AnalyticsMediaAudienceDimension>('country');
  const [metric, setMetric] = useState<AnalyticsMetric>('watchTime');
  // Local state, unlike the Audience tab's URL param — the drawer itself is
  // not URL-addressable, so a view param would outlive the drawer it styles.
  const [view, setView] = useState<'list' | 'map'>('list');

  const { search } = useAnalyticsRange();
  const { features } = useAnalyticsMeta();
  const rangeLabel =
    search.preset === 'custom'
      ? AnalyticsUtils.formatUtcDayRangeLabel(range)
      : ANALYTICS_PRESET_LABELS[search.preset];

  const showExpansion = FEATURE_FLAGS.analyticsExpansionEnabled;
  // Technology dimensions exist at media grain only once the gateway declares
  // features.hourly (api ADR-0015); the source group rides the expansion flag.
  const dimensionOptions: readonly AnalyticsMediaAudienceDimension[] = showExpansion
    ? features.hourly
      ? ANALYTICS_MEDIA_AUDIENCE_DIMENSIONS
      : ANALYTICS_MEDIA_AUDIENCE_DIMENSIONS_LEGACY
    : features.hourly
      ? [...ANALYTICS_GEO_DIMENSIONS, 'deviceType', 'platform', 'streamType']
      : ANALYTICS_GEO_DIMENSIONS;
  // Map presentation applies to geo dimensions only — same rule as the
  // Audience page (embedDomain forces the list and hides the toggle).
  const isGeoDimension = (ANALYTICS_GEO_DIMENSIONS as readonly string[]).includes(dimension);
  const effectiveView = isGeoDimension && view === 'map' ? 'map' : 'list';

  const { audience, isLoading, isError, error, refetch } = useAnalyticsMediaAudience(
    cid,
    row?.mediaId ?? null,
    dimension,
    range,
    metric
  );

  // Expansion queries stay hard-disabled (flag inside the hooks) while dark.
  const peaksQuery = useAnalyticsMediaPeaks(cid, row?.mediaId ?? null, range);
  // Retention is VOD-only — live rows pass null so no query ever fires.
  const retentionQuery = useAnalyticsMediaRetention(
    cid,
    row?.streamType === 'vod' ? row.mediaId : null,
    range
  );

  const rangePeak = peaksQuery.peaks?.data.rangePeak ?? null;
  const rangePeakIsProvisional =
    peaksQuery.peaks !== null && rangePeakProvisional(peaksQuery.peaks.data);

  return (
    <Drawer
      opened={row !== null}
      onClose={onClose}
      // Title decoration fallback is the display id; the wire string stays in keys and routes.
      title={row ? row.title ?? AnalyticsUtils.displayMediaId(row.mediaId) : ''}
      position="right"
      size="xl"
    >
      {row !== null && (
        <Stack gap="md">
          {/* Sub-account attribution rides network-scope rows (api ADR-0012). */}
          {row.label && (
            <Text size="sm" c="dimmed" mt={-8}>
              Account: {row.label}
            </Text>
          )}
          {onOpenStreamPage && (
            <Button variant="light" size="xs" w="fit-content" onClick={() => onOpenStreamPage(row)}>
              Open stream report
            </Button>
          )}
          {/* Headline totals come from the clicked row — same range, no refetch. */}
          {/* Cols arithmetic follows the OverviewCards showDelivery pattern:
              the flag-off layout is exactly the pre-expansion 3-up grid. */}
          <SimpleGrid
            cols={{ base: 1, xs: showExpansion ? 2 : 3, md: showExpansion ? 4 : 3 }}
            spacing="sm"
          >
            <StatTile label="Sessions" value={AnalyticsUtils.formatCount(row.sessions)} />
            <StatTile label="Watch time" value={AnalyticsUtils.formatWatchTime(row.watchTimeMs)} />
            <StatTile label="Unique viewers" value={AnalyticsUtils.formatCount(row.uniqueViewers)} />
            {showExpansion && (
              <StatTile
                label="Peak concurrent"
                value={
                  rangePeak !== null ? AnalyticsUtils.formatCount(rangePeak.peakConcurrent) : null
                }
                secondary={
                  rangePeak !== null ? (
                    <Text size="xs" c="dimmed" mt={2}>
                      {peakMinuteLabel(rangePeak.peakMinute)}
                      {rangePeakIsProvisional && ' (provisional)'}
                    </Text>
                  ) : undefined
                }
                isLoading={peaksQuery.isLoading}
              />
            )}
          </SimpleGrid>

          {showExpansion && (
            <MediaPeaksPanel
              peaks={peaksQuery.peaks}
              range={selectedRange}
              isLoading={peaksQuery.isLoading}
              isError={peaksQuery.isError}
              error={peaksQuery.error}
              onRetry={peaksQuery.refetch}
            />
          )}

          {showExpansion && row.streamType === 'vod' && (
            <MediaRetentionPanel
              retention={retentionQuery.retention}
              range={selectedRange}
              isLoading={retentionQuery.isLoading}
              isError={retentionQuery.isError}
              error={retentionQuery.error}
              onRetry={retentionQuery.refetch}
            />
          )}

          <Group gap="sm" wrap="wrap">
            <SegmentedControl
              size="xs"
              data={dimensionOptions.map((d) => ({ value: d, label: DIMENSION_LABELS[d] }))}
              value={dimension}
              onChange={(value) => setDimension(value as AnalyticsMediaAudienceDimension)}
            />
            <SegmentedControl
              size="xs"
              data={ANALYTICS_METRICS.map((m) => ({ value: m, label: ANALYTICS_METRIC_LABELS[m] }))}
              value={metric}
              onChange={(value) => setMetric(value as AnalyticsMetric)}
            />
            {isGeoDimension && (
              <SegmentedControl
                size="xs"
                data={[
                  { value: 'list', label: 'List' },
                  { value: 'map', label: 'Map' },
                ]}
                value={effectiveView}
                onChange={(value) => setView(value as 'list' | 'map')}
              />
            )}
          </Group>

          {effectiveView === 'map' ? (
            <Suspense fallback={<Skeleton height={380} />}>
              <AudienceMapPanel
                dimension={dimension as AnalyticsGeoDimension}
                metric={metric}
                audience={audience}
                range={selectedRange}
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={refetch}
                emptyMessage={GEO_EMPTY_MESSAGE}
                rangeLabel={rangeLabel}
              />
            </Suspense>
          ) : (
            <AudienceBreakdownPanel
              dimension={dimension}
              metric={metric}
              audience={audience}
              range={selectedRange}
              isLoading={isLoading}
              isError={isError}
              error={error}
              onRetry={refetch}
              emptyMessage={isGeoDimension ? GEO_EMPTY_MESSAGE : EMBED_EMPTY_MESSAGE}
            />
          )}
        </Stack>
      )}
    </Drawer>
  );
}
